<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <h2 class="text-xl font-semibold text-center mb-8">Konfirmasi Pembayaran</h2>
      <div class="space-y-4">
        <div>
          <label class="text-sm text-gray-600">Bank Tujuan</label>
          <p class="font-semibold">Mandiri</p>
        </div>
        
        <div>
          <label class="text-sm text-gray-600">Nomor Rekening</label>
          <div class="flex items-center space-x-2">
            <p class="font-semibold">1010012222277</p>
            <button 
              @click="copyToClipboard('1010012222277')"
              class="p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                v-if="!copied"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
          </div>
        </div>
        
        <div>
          <label class="text-sm text-gray-600">Nama Penerima</label>
          <p class="font-semibold">Birus Apitu</p>
        </div>
      </div>
      <!-- Upload Section -->
      <div class="mt-6">
        <div class="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <label class="block text-sm text-gray-600">
            Upload Bukti Transfer (Wajib)
          </label>
          <input
            type="file"
            @change="handleFileUpload"
            accept="image/*"
            class="mt-2 w-full text-sm text-gray-500
            file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0
            file:text-sm file:font-semibold
            file:bg-blue-50 file:text-blue-700
            hover:file:bg-blue-100"
          />
          <p v-if="paymentProof" class="mt-2 text-sm text-gray-600">
            File terpilih: {{ paymentProof.name }}
          </p>
        </div>

        <!-- Preview Image -->
        <div v-if="imagePreview" class="mt-4">
          <img :src="imagePreview" alt="Preview" class="max-h-48 rounded-lg mx-auto" />
        </div>

        <!-- Error Message -->
        <p v-if="error" class="mt-2 text-sm text-red-500">{{ error }}</p>
      </div>

      <!-- Payment Amount -->
      <div class="mt-6 text-center">
        <p class="text-gray-600">Total Pembayaran:</p>
        <p class="text-2xl font-bold mt-1">Rp {{ formatPrice(total) }}</p>
        <p class="text-sm text-gray-500 mt-2">
          Pastikan nominal transfer sudah sesuai
        </p>
      </div>

      <!-- Buttons -->
      <div class="mt-6 flex justify-center gap-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
        >
          Batal
        </button>
        <button
          @click="confirmPayment"
          :disabled="!paymentProof || isSubmitting"
          :class="[
            'px-4 py-2 rounded-md transition-colors',
            paymentProof && !isSubmitting
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          ]"
        >
          {{ isSubmitting ? 'Memproses...' : 'Konfirmasi Pembayaran' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { transactionService } from '../services/api'

export default {
  name: 'PaymentConfirmationModal',
  props: {
    cart: {
      type: Array,
      required: true
    },
    total: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      paymentProof: null,
      imagePreview: null,
      error: null,
      isSubmitting: false
    }
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('id-ID').format(price)
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) {
        this.paymentProof = null
        this.imagePreview = null
        return
      }

      // Validasi ukuran file (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        this.error = 'Ukuran file terlalu besar (maksimal 2MB)'
        event.target.value = null
        this.paymentProof = null
        this.imagePreview = null
        return
      }

      // Validasi tipe file
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']
      if (!allowedTypes.includes(file.type)) {
        this.error = 'Format file tidak didukung. Gunakan JPG atau PNG'
        event.target.value = null
        this.paymentProof = null
        this.imagePreview = null
        return
      }

      this.error = null
      this.paymentProof = file

      // Create preview
      const reader = new FileReader()
      reader.onload = e => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },

    async confirmPayment() {
  if (!this.paymentProof) {
    this.error = 'Harap upload bukti pembayaran'
    return
  }

  this.isSubmitting = true
  this.error = null

  try {
    const formData = new FormData()
    
    // Append cart data
    this.cart.forEach(item => {
      formData.append(`product_id[]`, item.product.id)
      formData.append(`amount[]`, item.quantity)
    })

    // Append single file sebagai array
    formData.append('attachment[]', this.paymentProof)

    const response = await transactionService.addTransaction(formData)
    
    if (response.data.success) {
      this.$emit('payment-confirmed')
      this.$emit('close')
    } else {
      throw new Error(response.data.message || 'Gagal membuat transaksi')
    }
  } catch (error) {
    console.error('Error details:', error.response?.data || error)
    this.error = error.response?.data?.message || 'Gagal membuat transaksi. Silakan coba lagi.'
  } finally {
    this.isSubmitting = false
  }
}
  },
  emits: ['close', 'payment-confirmed']
}
</script>